import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { AIWordingButton } from './AIWordingButton';
import { MetricInfo } from './MetricInfo';
import { 
  Plus, FileText, Eye, Download, Edit3, RefreshCw, CheckCircle, 
  Trash2, MoreHorizontal, Loader2, PoundSterling, XCircle, ArrowRightCircle,
  Send, Briefcase, ToggleLeft, ToggleRight, Upload
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { InvoiceProfitabilityWidget } from './InvoiceProfitabilityWidget';
import { InvoiceBulkUploadDialog } from './InvoiceBulkUpload';
import { InvoiceBulkDatesDialog } from './InvoiceBulkDates';
import PaymentScheduleEditor from './PaymentScheduleEditor';

const API = window.location.origin;

const getDepositAmount = (invoice) => {
  if (!invoice.deposit_enabled || !invoice.deposit_value) return null;
  const total = invoice.total || 0;
  if (invoice.deposit_type === 'percentage') return Math.round(total * (invoice.deposit_value / 100) * 100) / 100;
  if (invoice.deposit_type === 'fixed') return Math.min(invoice.deposit_value, total);
  return null;
};

const DepositConfig = ({ data, onChange }) => {
  const total = data.line_items?.reduce((sum, item) => sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_cost || 0)), 0) || 0;
  const depositAmount = data.deposit_type === 'percentage' 
    ? Math.round(total * (parseFloat(data.deposit_value || 0) / 100) * 100) / 100
    : Math.min(parseFloat(data.deposit_value || 0), total);
  const balanceRemaining = Math.round((total - depositAmount) * 100) / 100;

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="deposit-config">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <PoundSterling className="h-4 w-4" />
          Request Deposit
        </Label>
        <Switch 
          checked={data.deposit_enabled} 
          onCheckedChange={(checked) => onChange({ ...data, deposit_enabled: checked })}
          data-testid="deposit-toggle"
        />
      </div>
      
      {data.deposit_enabled && (
        <div className="space-y-3 pt-2 border-t">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Deposit Type</Label>
              <Select value={data.deposit_type} onValueChange={(v) => onChange({ ...data, deposit_type: v })}>
                <SelectTrigger data-testid="deposit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{data.deposit_type === 'percentage' ? 'Percentage' : 'Amount (£)'}</Label>
              <Input 
                type="number" 
                value={data.deposit_value} 
                onChange={(e) => onChange({ ...data, deposit_value: e.target.value })}
                min={0} 
                max={data.deposit_type === 'percentage' ? 100 : undefined}
                step={data.deposit_type === 'percentage' ? 5 : 50}
                data-testid="deposit-value-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Deposit Purpose</Label>
            <Select value={data.deposit_purpose} onValueChange={(v) => onChange({ ...data, deposit_purpose: v })}>
              <SelectTrigger data-testid="deposit-purpose-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="materials">Materials</SelectItem>
                <SelectItem value="labour_booking">Labour Booking</SelectItem>
                <SelectItem value="project_commencement">Project Commencement</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {data.deposit_purpose === 'custom' && (
            <div>
              <Label className="text-xs">Custom Purpose</Label>
              <Input 
                value={data.deposit_custom_purpose || ''} 
                onChange={(e) => onChange({ ...data, deposit_purpose: e.target.value || 'custom' })}
                placeholder="e.g. Specialist equipment"
                data-testid="deposit-custom-purpose"
              />
            </div>
          )}
          
          {total > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm space-y-1" data-testid="deposit-preview">
              <div className="flex justify-between">
                <span>Project Total:</span>
                <span className="font-semibold">£{total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-amber-700 font-semibold">
                <span>Deposit Due Now:</span>
                <span>£{depositAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Remaining Balance:</span>
                <span>£{balanceRemaining.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getPdfUrl = (pdfUrl) => {
  if (!pdfUrl) return null;
  if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) return pdfUrl;
  return `${API}${pdfUrl}`;
};

// Status color maps
const quoteStatusConfig = {
  draft:   { label: 'Draft',   bg: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Draft',   bg: 'bg-gray-100 text-gray-700' },
  sent:    { label: 'Sent',    bg: 'bg-blue-100 text-blue-700' },
  accepted:{ label: 'Accepted',bg: 'bg-green-100 text-green-700' },
  rejected:{ label: 'Rejected',bg: 'bg-red-100 text-red-700' },
  declined:{ label: 'Rejected',bg: 'bg-red-100 text-red-700' },
  converted_to_job: { label: 'Converted', bg: 'bg-purple-100 text-purple-700' },
  converted: { label: 'Converted', bg: 'bg-purple-100 text-purple-700' },
  complete: { label: 'Complete', bg: 'bg-emerald-100 text-emerald-700' },
};

const invoiceStatusConfig = {
  draft:   { label: 'Draft',   bg: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Draft',   bg: 'bg-gray-100 text-gray-700' },
  sent:    { label: 'Sent',    bg: 'bg-blue-100 text-blue-700' },
  deposit_paid: { label: 'Deposit Paid', bg: 'bg-amber-100 text-amber-700' },
  part_paid: { label: 'Part Paid', bg: 'bg-orange-100 text-orange-700' },
  paid:    { label: 'Paid',    bg: 'bg-green-100 text-green-700' },
  overdue: { label: 'Overdue', bg: 'bg-red-100 text-red-700' },
  cancelled:{ label: 'Cancelled', bg: 'bg-gray-100 text-gray-500' },
};

const StatusBadge = ({ status, configMap }) => {
  const config = configMap[status] || { label: status, bg: 'bg-gray-100 text-gray-700' };
  return <Badge className={config.bg}>{config.label}</Badge>;
};

export const QuotesInvoicesTab = ({ token, customers, enquiries = [], quotes: parentQuotes, invoices: parentInvoices, onInvoiceCreated, onQuoteCreated }) => {
  const [filterStatus, setFilterStatus] = useState('invoice');

  // Reset invoice pagination whenever the filter changes so we never land on an empty page
  useEffect(() => {
    if (typeof setInvoicePage === 'function') setInvoicePage(1);
  }, [filterStatus]);
  const [invoices, setInvoices] = useState(parentInvoices || []);
  const [quotes, setQuotes] = useState(parentQuotes || []);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [quotesLoading, setQuotesLoading] = useState(false);
  
  // Invoice dialogs
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [isBulkOcrOpen, setIsBulkOcrOpen] = useState(false);
  const [isBulkDatesOpen, setIsBulkDatesOpen] = useState(false);
  const [suggestedReference, setSuggestedReference] = useState('');
  // Invoice list pagination — resets whenever the active filter changes.
  const [invoicePage, setInvoicePage] = useState(1);
  const INVOICES_PER_PAGE = 20;
  const [isJobPickerOpen, setIsJobPickerOpen] = useState(false);
  const [jobPickerSearch, setJobPickerSearch] = useState("");
  const [isPullingLines, setIsPullingLines] = useState(false);
  const [isEditInvoiceOpen, setIsEditInvoiceOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_name: '', customer_email: '', customer_address: '', description: '',
    line_items: [{ description: '', quantity: 1, unit_cost: 0 }], notes: '', payment_days: 7, date: null, job_id: null,
    deposit_enabled: false, deposit_type: 'percentage', deposit_value: 40, deposit_purpose: 'materials', terms_override: ''
  });
  
  // Quote dialogs
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false);
  const [isEditQuoteOpen, setIsEditQuoteOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [isSavingQuote, setIsSavingQuote] = useState(false);
  const [newQuote, setNewQuote] = useState({
    customer_name: '', customer_email: '', customer_address: '', description: '',
    line_items: [{ description: '', quantity: 1, unit_cost: 0 }], notes: '', valid_days: 30, date: null, job_id: null
  });

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  
  // Deposit payment dialog
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    type: 'deposit', amount: '', date: new Date().toISOString().split('T')[0],
    method: 'bank_transfer', notes: ''
  });
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  const openPaymentDialog = (inv, type = 'deposit') => {
    const defaultAmount = type === 'deposit' 
      ? (inv.deposit_amount || getDepositAmount(inv) || 0) 
      : (inv.balance_remaining || inv.total || 0);
    setPaymentInvoice(inv);
    setPaymentData({
      type, amount: defaultAmount.toFixed(2),
      date: new Date().toISOString().split('T')[0],
      method: 'bank_transfer', notes: ''
    });
    setIsPaymentDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentInvoice || !paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    setIsRecordingPayment(true);
    try {
      await axios.post(`${API}/api/admin/invoices/${paymentInvoice.id}/payments`, {
        type: paymentData.type,
        amount: parseFloat(paymentData.amount),
        date: paymentData.date,
        method: paymentData.method,
        notes: paymentData.notes
      }, authHeaders);
      toast.success('Payment recorded successfully');
      setIsPaymentDialogOpen(false);
      // Regenerate PDF to reflect payment
      try {
        await axios.post(`${API}/api/admin/invoices/${paymentInvoice.id}/regenerate-pdf`, {}, authHeaders);
      } catch { /* PDF regen is optional */ }
      fetchInvoices();
    } catch (err) {
      toast.error('Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };
  
  // Check for job data passed from Jobs tab
  useEffect(() => {
    const checkSessionStorage = () => {
      const createQuoteData = sessionStorage.getItem('createQuoteFor');
      if (createQuoteData) {
        try {
          const jobData = JSON.parse(createQuoteData);
          setNewQuote(prev => ({
            ...prev,
            customer_name: jobData.customerName || '',
            customer_email: jobData.customerEmail || '',
            description: jobData.description || jobData.service || '',
            job_id: jobData.jobId || jobData.customerId || null
          }));
          setFilterStatus('quote');
          setIsCreateQuoteOpen(true);
          sessionStorage.removeItem('createQuoteFor');
          toast.info(`Creating quote for ${jobData.customerName}`);
        } catch (e) { console.error('Failed to parse quote data:', e); }
      }
      
      const createInvoiceData = sessionStorage.getItem('createInvoiceFor');
      if (createInvoiceData) {
        try {
          const jobData = JSON.parse(createInvoiceData);
          setNewInvoice(prev => ({
            ...prev,
            customer_name: jobData.customerName || '',
            customer_email: jobData.customerEmail || '',
            description: jobData.description || jobData.service || '',
            job_id: jobData.jobId || jobData.customerId || null
          }));
          setFilterStatus('invoice');
          setIsCreateInvoiceOpen(true);
          sessionStorage.removeItem('createInvoiceFor');
          toast.info(`Creating invoice for ${jobData.customerName}`);
        } catch (e) { console.error('Failed to parse invoice data:', e); }
      }
    };
    checkSessionStorage();
    const interval = setInterval(checkSessionStorage, 500);
    return () => clearInterval(interval);
  }, []);

  // Sync with parent data
  useEffect(() => {
    if (parentQuotes) {
      const sorted = [...parentQuotes].sort((a, b) => (b.created_at || b.date || '').localeCompare(a.created_at || a.date || ''));
      setQuotes(sorted);
    }
  }, [parentQuotes]);

  // Auto-suggest the next SHS-YYYY-NNN reference whenever the Create Invoice
  // dialog opens or the user changes the invoice date (which can shift the year
  // and reset the counter).
  useEffect(() => {
    if (!isCreateInvoiceOpen) return;
    const year = newInvoice.date ? parseInt(newInvoice.date.slice(0, 4), 10) : null;
    const url = year
      ? `${API}/api/admin/invoices/next-reference?year=${year}`
      : `${API}/api/admin/invoices/next-reference`;
    axios
      .get(url, authHeaders)
      .then((res) => setSuggestedReference(res.data?.reference || ''))
      .catch(() => setSuggestedReference(''));
  }, [isCreateInvoiceOpen, newInvoice.date]); // eslint-disable-line react-hooks/exhaustive-deps

  // STRICT REFERENCE SORT — per Sandy's "Primary Sorting by Reference" rule.
  // Parse SHS-YYYY-NNN and sort:
  //   1. Year DESC (2026 above 2025 above 2024).
  //   2. Sequence number DESC numerically (030 above 029 above 001).
  // Invoices whose reference does not match the SHS-YYYY-NNN pattern sink to
  // the bottom (year=-Infinity, seq=-Infinity) and are tiebroken by reference
  // string so they remain deterministic.
  const _parseRefYearSeq = (ref) => {
    const m = /^SHS-(\d{4})-(\d+)$/.exec(ref || '');
    if (!m) return { year: -Infinity, seq: -Infinity };
    return { year: parseInt(m[1], 10), seq: parseInt(m[2], 10) };
  };
  const _invoiceSortCompare = (a, b) => {
    const pa = _parseRefYearSeq(a.reference);
    const pb = _parseRefYearSeq(b.reference);
    if (pa.year !== pb.year) return pb.year - pa.year;   // Year DESC
    if (pa.seq !== pb.seq) return pb.seq - pa.seq;       // Sequence DESC (numeric)
    // Final tiebreaker — lexicographic reference DESC (keeps ordering stable
    // for any non-conforming refs that reached this branch).
    return (b.reference || '').localeCompare(a.reference || '');
  };

  useEffect(() => {
    if (parentInvoices) {
      const sorted = [...parentInvoices].sort(_invoiceSortCompare);
      setInvoices(sorted);
    }
  }, [parentInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Self-fetch if parent data is empty (fallback)
  const doFetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/invoices`, authHeaders);
      const sorted = (response.data.invoices || []).sort(_invoiceSortCompare);
      setInvoices(sorted);
    } catch (error) { console.error("Error fetching invoices:", error); }
    finally { setInvoicesLoading(false); }
  }, [authHeaders]);

  const doFetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/quotes`, authHeaders);
      const sorted = (response.data.quotes || []).sort((a, b) => (b.created_at || b.date || '').localeCompare(a.created_at || a.date || ''));
      setQuotes(sorted);
    } catch (error) { console.error("Error fetching quotes:", error); }
    finally { setQuotesLoading(false); }
  }, [authHeaders]);

  useEffect(() => {
    if (token) {
      // Fetch own data if parent hasn't provided it yet
      if (!parentInvoices || parentInvoices.length === 0) doFetchInvoices();
      if (!parentQuotes || parentQuotes.length === 0) doFetchQuotes();
    }
  }, [token, parentInvoices, parentQuotes, doFetchInvoices, doFetchQuotes]);

  const fetchInvoices = async () => {
    doFetchInvoices();
    if (onInvoiceCreated) onInvoiceCreated();
  };

  const fetchQuotes = async () => {
    doFetchQuotes();
    if (onQuoteCreated) onQuoteCreated();
  };

  // CRUD handlers
  const handlePullFromJob = async (job) => {
    setIsPullingLines(true);
    try {
      const res = await axios.get(`${API}/api/admin/jobs/${job.id}/suggested-invoice-lines`, authHeaders);
      const suggested = res.data?.lines || [];
      if (suggested.length === 0) {
        toast.info("No quote lines or received materials on this job yet.");
        setIsPullingLines(false);
        return;
      }
      setNewInvoice(prev => {
        const existingBlank = prev.line_items.filter(i => (i.description || '').trim() !== '');
        return {
          ...prev,
          job_id: job.id,
          customer_name: prev.customer_name || job.name || '',
          customer_address: prev.customer_address || job.address || '',
          customer_email: prev.customer_email || job.email || '',
          description: prev.description || `Works completed — ${job.service || ''}`.trim(),
          line_items: [
            ...existingBlank,
            ...suggested.map(l => ({ description: l.description, quantity: l.quantity, unit_cost: l.unit_cost })),
          ],
        };
      });
      toast.success(`Added ${suggested.length} line(s) from job`);
      setIsJobPickerOpen(false);
      setJobPickerSearch("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to pull lines");
    } finally {
      setIsPullingLines(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!newInvoice.customer_name) { toast.error("Customer name is required"); return; }
    setIsSavingInvoice(true);
    try {
      const payload = {
        customer_name: newInvoice.customer_name, customer_email: newInvoice.customer_email,
        customer_address: newInvoice.customer_address, description: newInvoice.description,
        line_items: newInvoice.line_items.filter(i => i.description), notes: newInvoice.notes,
        payment_days: newInvoice.payment_days, date: newInvoice.date || null,
        terms_override: newInvoice.terms_override || null
      };
      if (newInvoice.deposit_enabled) {
        payload.deposit_enabled = true;
        payload.deposit_type = newInvoice.deposit_type;
        payload.deposit_value = parseFloat(newInvoice.deposit_value) || 0;
        payload.deposit_purpose = newInvoice.deposit_purpose;
      }
      await axios.post(`${API}/api/admin/invoices`, payload, authHeaders);
      toast.success("Invoice created!");
      setIsCreateInvoiceOpen(false);
      setNewInvoice({ customer_name: '', customer_email: '', customer_address: '', description: '', line_items: [{ description: '', quantity: 1, unit_cost: 0 }], notes: '', payment_days: 7, date: null, deposit_enabled: false, deposit_type: 'percentage', deposit_value: 40, deposit_purpose: 'materials', terms_override: '' });
      fetchInvoices();
      if (onInvoiceCreated) onInvoiceCreated();
    } catch (error) { toast.error("Failed to create invoice"); }
    finally { setIsSavingInvoice(false); }
  };

  const openEditInvoice = (invoice) => {
    setEditingInvoice({ 
      ...invoice, 
      line_items: invoice.line_items?.length > 0 ? invoice.line_items : [{ description: '', quantity: 1, unit_cost: 0 }], 
      date: invoice.date ? '' : '',
      deposit_enabled: invoice.deposit_enabled || false,
      deposit_type: invoice.deposit_type || 'percentage',
      deposit_value: invoice.deposit_value || 40,
      deposit_purpose: invoice.deposit_purpose || 'materials'
    });
    setIsEditInvoiceOpen(true);
  };

  const saveEditedInvoice = async () => {
    if (!editingInvoice) return;
    setIsSavingInvoice(true);
    try {
      const payload = {
        customer_name: editingInvoice.customer_name, customer_address: editingInvoice.customer_address,
        description: editingInvoice.description, line_items: editingInvoice.line_items,
        payment_days: editingInvoice.payment_days, date: editingInvoice.date || null,
        terms_override: editingInvoice.terms_override || null,
        deposit_enabled: editingInvoice.deposit_enabled || false,
        deposit_type: editingInvoice.deposit_type,
        deposit_value: parseFloat(editingInvoice.deposit_value) || 0,
        deposit_purpose: editingInvoice.deposit_purpose
      };
      await axios.put(`${API}/api/admin/invoices/${editingInvoice.id}`, payload, authHeaders);
      toast.success("Invoice updated and PDF regenerated!");
      setIsEditInvoiceOpen(false); setEditingInvoice(null); fetchInvoices();
    } catch (error) { toast.error("Failed to update invoice"); }
    finally { setIsSavingInvoice(false); }
  };

  const handleCreateQuote = async () => {
    if (!newQuote.customer_name) { toast.error("Customer name is required"); return; }
    setIsSavingQuote(true);
    try {
      await axios.post(`${API}/api/admin/quotes`, {
        customer_name: newQuote.customer_name, customer_email: newQuote.customer_email,
        customer_address: newQuote.customer_address, description: newQuote.description,
        line_items: newQuote.line_items.filter(i => i.description), notes: newQuote.notes,
        valid_days: newQuote.valid_days, date: newQuote.date || null
      }, authHeaders);
      toast.success("Quote created!");
      setIsCreateQuoteOpen(false);
      setNewQuote({ customer_name: '', customer_email: '', customer_address: '', description: '', line_items: [{ description: '', quantity: 1, unit_cost: 0 }], notes: '', valid_days: 30, date: null });
      fetchQuotes();
      if (onQuoteCreated) onQuoteCreated();
    } catch (error) { toast.error("Failed to create quote"); }
    finally { setIsSavingQuote(false); }
  };

  const openEditQuote = (quote) => {
    setEditingQuote({ ...quote, line_items: quote.line_items?.length > 0 ? quote.line_items : [{ description: '', quantity: 1, unit_cost: 0 }], date: quote.date ? '' : '' });
    setIsEditQuoteOpen(true);
  };

  const saveEditedQuote = async () => {
    if (!editingQuote) return;
    setIsSavingQuote(true);
    try {
      await axios.put(`${API}/api/admin/quotes/${editingQuote.id}`, {
        customer_name: editingQuote.customer_name, customer_address: editingQuote.customer_address,
        description: editingQuote.description, line_items: editingQuote.line_items,
        valid_days: editingQuote.valid_days, date: editingQuote.date || null
      }, authHeaders);
      toast.success("Quote updated and PDF regenerated!");
      setIsEditQuoteOpen(false); setEditingQuote(null); fetchQuotes();
    } catch (error) { toast.error("Failed to update quote"); }
    finally { setIsSavingQuote(false); }
  };

  // Quote actions
  const handleRejectQuote = async (quote) => {
    if (!confirm(`Reject quote ${quote.reference}?`)) return;
    try {
      await axios.patch(`${API}/api/admin/quotes/${quote.id}/status?status=rejected`, {}, authHeaders);
      toast.success('Quote rejected');
      fetchQuotes();
    } catch (error) { toast.error('Failed to reject quote'); }
  };

  const handleConvertToJob = async (quote) => {
    if (!confirm(`Convert quote ${quote.reference} to a job in the pipeline?`)) return;
    try {
      const res = await axios.post(`${API}/api/admin/quotes/${quote.id}/convert-to-job`, {}, authHeaders);
      toast.success(`Job created from ${quote.reference}`);
      fetchQuotes();
      if (onQuoteCreated) onQuoteCreated(); // refresh jobs list
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to convert to job'); }
  };

  // Line item helpers
  const addLineItem = (setter) => setter(prev => ({ ...prev, line_items: [...prev.line_items, { description: '', quantity: 1, unit_cost: 0 }] }));
  const calculateTotal = (items) => items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);

  // Shared line items editor
  const LineItemsEditor = ({ items, onChange }) => (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="grid grid-cols-4 gap-2">
          <Input placeholder="Description" value={item.description} onChange={(e) => {
            const updated = [...items]; updated[i].description = e.target.value; onChange(updated);
          }} className="col-span-2" />
          <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => {
            const updated = [...items]; updated[i].quantity = parseFloat(e.target.value) || 0; onChange(updated);
          }} />
          <Input type="number" placeholder="Price" value={item.unit_cost} onChange={(e) => {
            const updated = [...items]; updated[i].unit_cost = parseFloat(e.target.value) || 0; onChange(updated);
          }} />
        </div>
      ))}
    </div>
  );

  // Active quotes (exclude rejected/converted for the main list count)
  const activeQuotes = quotes.filter(q => !['rejected', 'declined', 'converted_to_job', 'converted', 'complete'].includes(q.status));
  
  // Invoice counts
  const unpaidInvoices = invoices.filter(i => !['paid', 'cancelled'].includes(i.status));
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const overdueInvoices = invoices.filter(i => i.status === 'overdue');
  const cancelledInvoices = invoices.filter(i => i.status === 'cancelled');

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-3 flex-wrap">
        {/* Invoice tabs */}
        <Button variant={filterStatus === 'invoice' ? "default" : "outline"} size="sm"
          onClick={() => setFilterStatus('invoice')} data-testid="invoices-tab-btn">
          Invoices ({unpaidInvoices.length})
        </Button>
        {/* Paid sub-tab always visible so it's discoverable even at 0; renders
            an empty-state message inside when there are no paid invoices. */}
        <Button variant={filterStatus === 'invoice_paid' ? "default" : "outline"} size="sm"
          onClick={() => setFilterStatus('invoice_paid')} data-testid="paid-invoices-btn"
          title="Invoices with status Paid — all time. Counts INVOICES; the Dashboard 'Paid Jobs' stat counts jobs, so the two can legitimately differ.">
          Paid Invoices ({paidInvoices.length})
        </Button>
        {overdueInvoices.length > 0 && (
          <Button variant={filterStatus === 'invoice_overdue' ? "default" : "outline"} size="sm"
            onClick={() => setFilterStatus('invoice_overdue')} data-testid="overdue-invoices-btn">
            Overdue ({overdueInvoices.length})
          </Button>
        )}
        {cancelledInvoices.length > 0 && (
          <Button variant={filterStatus === 'invoice_cancelled' ? "default" : "outline"} size="sm"
            onClick={() => setFilterStatus('invoice_cancelled')} data-testid="cancelled-invoices-btn">
            Cancelled ({cancelledInvoices.length})
          </Button>
        )}
        
        {/* Divider */}
        <div className="w-px bg-border mx-1" />
        
        {/* Quote tabs */}
        <Button variant={filterStatus === 'quote' ? "default" : "outline"} size="sm"
          onClick={() => setFilterStatus('quote')} data-testid="quotes-tab-btn">
          Quotes ({activeQuotes.length})
        </Button>
        {quotes.some(q => ['rejected', 'declined'].includes(q.status)) && (
          <Button variant={filterStatus === 'quote_rejected' ? "default" : "outline"} size="sm"
            onClick={() => setFilterStatus('quote_rejected')} data-testid="rejected-quotes-btn">
            Rejected ({quotes.filter(q => ['rejected', 'declined'].includes(q.status)).length})
          </Button>
        )}
        {quotes.some(q => ['converted_to_job', 'converted'].includes(q.status)) && (
          <Button variant={filterStatus === 'quote_converted' ? "default" : "outline"} size="sm"
            onClick={() => setFilterStatus('quote_converted')} data-testid="converted-quotes-btn">
            Converted ({quotes.filter(q => ['converted_to_job', 'converted'].includes(q.status)).length})
          </Button>
        )}
        {quotes.some(q => q.status === 'complete') && (
          <Button variant={filterStatus === 'quote_complete' ? "default" : "outline"} size="sm"
            onClick={() => setFilterStatus('quote_complete')} data-testid="complete-quotes-btn">
            Complete ({quotes.filter(q => q.status === 'complete').length})
          </Button>
        )}
      </div>
      
      {/* ==================== INVOICES SECTION ==================== */}
      {filterStatus.startsWith('invoice') && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading">
                {filterStatus === 'invoice_paid' ? <>Paid Invoices <MetricInfo metricKey="paid_invoices" /></> : filterStatus === 'invoice_overdue' ? 'Overdue Invoices' : filterStatus === 'invoice_cancelled' ? 'Cancelled Invoices' : 'Invoices'}
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={() => setIsBulkDatesOpen(true)} size="sm" variant="outline" data-testid="invoice-bulk-dates-btn">
                  Fix dates
                </Button>
                <Button onClick={() => setIsBulkOcrOpen(true)} size="sm" variant="outline" data-testid="invoice-bulk-upload-btn">
                  <Upload className="h-4 w-4 mr-1" />Bulk Upload
                </Button>
                <Button onClick={() => setIsCreateInvoiceOpen(true)} size="sm" data-testid="create-invoice-btn">
                  <Plus className="h-4 w-4 mr-1" />Create Invoice
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : (() => {
              let filtered = invoices;
              if (filterStatus === 'invoice_paid') filtered = paidInvoices;
              else if (filterStatus === 'invoice_overdue') filtered = overdueInvoices;
              else if (filterStatus === 'invoice_cancelled') filtered = cancelledInvoices;
              else filtered = unpaidInvoices;
              
              if (filtered.length === 0) return <p className="text-center py-8 text-muted-foreground">No invoices found</p>;
              
              // Pagination — 20 per page
              const totalPages = Math.max(1, Math.ceil(filtered.length / INVOICES_PER_PAGE));
              const safePage = Math.min(Math.max(1, invoicePage), totalPages);
              const currentPage = safePage;
              const pageStart = (safePage - 1) * INVOICES_PER_PAGE;
              const pageRows = filtered.slice(pageStart, pageStart + INVOICES_PER_PAGE);
              
              return (
              <div className="space-y-2">
                {pageRows.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50" data-testid={`invoice-${inv.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-medium">{inv.reference}</p>
                        <StatusBadge status={inv.status} configMap={invoiceStatusConfig} />
                        {inv.deposit_enabled && (
                          <Badge 
                            className={
                              inv.deposit_paid || inv.status === 'deposit_paid' 
                                ? 'bg-green-100 text-green-700 border-green-300' 
                                : inv.status === 'paid' 
                                  ? 'bg-green-100 text-green-700 border-green-300'
                                  : 'bg-amber-50 text-amber-600 border-amber-200'
                            } 
                            variant="outline" 
                            data-testid={`deposit-badge-${inv.id}`}
                          >
                            {inv.deposit_paid || inv.status === 'deposit_paid' || inv.status === 'paid'
                              ? `Deposit Paid` 
                              : `Deposit: £${(inv.deposit_amount || getDepositAmount(inv) || 0).toFixed(2)}`}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{inv.customer_name}</p>
                      <p className="text-xs text-muted-foreground" data-testid={`invoice-date-${inv.id}`}>
                        {(() => {
                          // Mirror the sort-key chain so card order matches displayed date.
                          const raw = inv.invoice_date || inv.date || inv.paid_at || inv.created_at;
                          if (!raw) return '';
                          try { return new Date(raw).toLocaleDateString('en-GB'); } catch { return raw; }
                        })()}
                      </p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="text-lg font-bold">£{inv.total?.toFixed(2)}</p>
                      {inv.amount_paid > 0 && inv.status !== 'paid' && (
                        <p className="text-xs text-green-600">Paid: £{inv.amount_paid.toFixed(2)}</p>
                      )}
                      {inv.balance_remaining > 0 && inv.status !== 'paid' && (
                        <p className="text-xs text-amber-600">Balance: £{inv.balance_remaining.toFixed(2)}</p>
                      )}
                      {inv.deposit_enabled && !inv.deposit_paid && inv.status !== 'deposit_paid' && inv.status !== 'paid' && !inv.amount_paid && (
                        <p className="text-xs text-amber-600">Deposit: £{(inv.deposit_amount || getDepositAmount(inv) || 0).toFixed(2)}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {inv.pdf_url && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => window.open(getPdfUrl(inv.pdf_url), '_blank')} title="View PDF" data-testid={`invoice-view-${inv.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { const a = document.createElement('a'); a.href = getPdfUrl(inv.pdf_url); a.download = `${inv.reference}.pdf`; a.click(); }} title="Download" data-testid={`invoice-download-${inv.id}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`invoice-menu-${inv.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditInvoice(inv)} data-testid="invoice-edit-btn">
                            <Edit3 className="h-4 w-4 mr-2" />Edit & Regenerate PDF
                          </DropdownMenuItem>
                          {inv.pdf_url && (
                            <DropdownMenuItem onClick={async () => {
                              try { await axios.post(`${API}/api/admin/invoices/${inv.id}/regenerate-pdf`, {}, authHeaders); toast.success('PDF regenerated!'); fetchInvoices(); }
                              catch (err) { toast.error('Failed to regenerate PDF'); }
                            }}><RefreshCw className="h-4 w-4 mr-2" />Regenerate PDF</DropdownMenuItem>
                          )}
                          {!inv.pdf_url && (
                            <DropdownMenuItem onClick={async () => {
                              try { await axios.post(`${API}/api/admin/invoices/${inv.id}/regenerate-pdf`, {}, authHeaders); toast.success('PDF generated!'); fetchInvoices(); }
                              catch (err) { toast.error('Failed to generate PDF'); }
                            }}><FileText className="h-4 w-4 mr-2" />Generate PDF</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {inv.status !== 'sent' && inv.status !== 'paid' && (
                            <DropdownMenuItem onClick={async () => {
                              await axios.patch(`${API}/api/admin/invoices/${inv.id}`, { status: 'sent' }, authHeaders);
                              toast.success('Marked as sent'); fetchInvoices();
                            }}><Send className="h-4 w-4 mr-2" />Mark as Sent</DropdownMenuItem>
                          )}
                          {inv.deposit_enabled && !inv.deposit_paid && inv.status !== 'deposit_paid' && inv.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => openPaymentDialog(inv, 'deposit')} data-testid={`mark-deposit-paid-${inv.id}`}>
                              <PoundSterling className="h-4 w-4 mr-2 text-amber-600" />Record Deposit Payment
                            </DropdownMenuItem>
                          )}
                          {(inv.deposit_paid || inv.status === 'deposit_paid') && inv.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => openPaymentDialog(inv, 'final')}>
                              <PoundSterling className="h-4 w-4 mr-2 text-green-600" />Record Final Payment
                            </DropdownMenuItem>
                          )}
                          {inv.status !== 'paid' && !inv.deposit_enabled && (
                            <DropdownMenuItem onClick={() => openPaymentDialog(inv, 'full')} data-testid={`record-payment-btn-${inv.id}`}>
                              <PoundSterling className="h-4 w-4 mr-2 text-green-600" />Record Payment
                            </DropdownMenuItem>
                          )}
                          {inv.status !== 'overdue' && inv.status !== 'paid' && (
                            <DropdownMenuItem onClick={async () => {
                              await axios.patch(`${API}/api/admin/invoices/${inv.id}`, { status: 'overdue' }, authHeaders);
                              toast.success('Marked as overdue'); fetchInvoices();
                            }}><XCircle className="h-4 w-4 mr-2 text-red-600" />Mark Overdue</DropdownMenuItem>
                          )}
                          {inv.status !== 'cancelled' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={async () => {
                                if (confirm('Cancel this invoice?')) {
                                  await axios.patch(`${API}/api/admin/invoices/${inv.id}`, { status: 'cancelled' }, authHeaders);
                                  toast.success('Invoice cancelled'); fetchInvoices();
                                }
                              }} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Cancel Invoice</DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={async () => {
                            if (confirm('Delete this invoice permanently?')) {
                              await axios.delete(`${API}/api/admin/invoices/${inv.id}`, authHeaders);
                              toast.success('Invoice deleted'); fetchInvoices();
                            }
                          }} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete Permanently</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
                {/* Pagination controls — only shown when more than one page */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-3 border-t" data-testid="invoice-pagination">
                    <p className="text-xs text-muted-foreground">
                      Page {currentPage} of {totalPages} · {filtered.length} invoice{filtered.length === 1 ? "" : "s"}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setInvoicePage(currentPage - 1)} data-testid="invoice-page-prev">
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setInvoicePage(currentPage + 1)} data-testid="invoice-page-next">
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
      
      {/* ==================== QUOTES SECTION ==================== */}
      {filterStatus.startsWith('quote') && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading">
                {filterStatus === 'quote_rejected' ? 'Rejected Quotes' : filterStatus === 'quote_converted' ? 'Converted Quotes' : filterStatus === 'quote_complete' ? 'Completed Quotes' : 'Quotes'}
              </CardTitle>
              <Button onClick={() => setIsCreateQuoteOpen(true)} size="sm" data-testid="create-quote-btn">
                <Plus className="h-4 w-4 mr-1" />Create Quote
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {quotesLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  let filtered = quotes;
                  if (filterStatus === 'quote_rejected') filtered = quotes.filter(q => ['rejected', 'declined'].includes(q.status));
                  else if (filterStatus === 'quote_converted') filtered = quotes.filter(q => ['converted_to_job', 'converted'].includes(q.status));
                  else if (filterStatus === 'quote_complete') filtered = quotes.filter(q => q.status === 'complete');
                  else filtered = activeQuotes;

                  if (filtered.length === 0) return <p className="text-center py-8 text-muted-foreground">No quotes found</p>;

                  return filtered.map(quote => (
                    <div key={quote.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50" data-testid={`quote-${quote.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="font-medium">{quote.reference}</p>
                          <StatusBadge status={quote.status} configMap={quoteStatusConfig} />
                        </div>
                        <p className="text-sm text-muted-foreground">{quote.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{quote.created_at ? new Date(quote.created_at).toLocaleDateString('en-GB') : ''}</p>
                      </div>
                      <div className="text-right mr-4">
                        <p className="text-lg font-bold">£{quote.total?.toFixed(2)}</p>
                      </div>
                      <div className="flex gap-1">
                        {quote.pdf_url && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => window.open(getPdfUrl(quote.pdf_url), '_blank')} title="View PDF" data-testid={`quote-view-${quote.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { const a = document.createElement('a'); a.href = getPdfUrl(quote.pdf_url); a.download = `${quote.reference}.pdf`; a.click(); }} title="Download" data-testid={`quote-download-${quote.id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`quote-menu-${quote.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditQuote(quote)} data-testid="quote-edit-btn">
                              <Edit3 className="h-4 w-4 mr-2" />Edit & Regenerate PDF
                            </DropdownMenuItem>
                            {quote.pdf_url && (
                              <DropdownMenuItem onClick={async () => {
                                try { await axios.post(`${API}/api/admin/quotes/${quote.id}/regenerate-pdf`, {}, authHeaders); toast.success('PDF regenerated!'); fetchQuotes(); }
                                catch (error) { toast.error('Failed to regenerate PDF'); }
                              }}><RefreshCw className="h-4 w-4 mr-2" />Regenerate PDF</DropdownMenuItem>
                            )}
                            {!quote.pdf_url && (
                              <DropdownMenuItem onClick={async () => {
                                try { await axios.post(`${API}/api/admin/quotes/${quote.id}/regenerate-pdf`, {}, authHeaders); toast.success('PDF generated!'); fetchQuotes(); }
                                catch (error) { toast.error('Failed to generate PDF'); }
                              }}><FileText className="h-4 w-4 mr-2" />Generate PDF</DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {/* Status actions - context-sensitive */}
                            {!['accepted', 'converted_to_job', 'converted'].includes(quote.status) && (
                              <DropdownMenuItem onClick={async () => {
                                await axios.patch(`${API}/api/admin/quotes/${quote.id}/status?status=sent`, {}, authHeaders);
                                toast.success('Marked as sent'); fetchQuotes();
                              }}><Send className="h-4 w-4 mr-2" />Mark as Sent</DropdownMenuItem>
                            )}
                            {!['accepted', 'converted_to_job', 'converted'].includes(quote.status) && (
                              <DropdownMenuItem onClick={async () => {
                                await axios.patch(`${API}/api/admin/quotes/${quote.id}/status?status=accepted`, {}, authHeaders);
                                toast.success('Marked as accepted'); fetchQuotes();
                              }}><CheckCircle className="h-4 w-4 mr-2 text-green-600" />Mark Accepted</DropdownMenuItem>
                            )}
                            {/* Convert to Job - only for accepted quotes */}
                            {quote.status === 'accepted' && (
                              <DropdownMenuItem onClick={() => handleConvertToJob(quote)} data-testid="quote-convert-job-btn">
                                <Briefcase className="h-4 w-4 mr-2 text-purple-600" />Move to Job Pipeline
                              </DropdownMenuItem>
                            )}
                            {/* Mark Complete - for accepted or converted quotes */}
                            {['accepted', 'converted_to_job', 'converted'].includes(quote.status) && (
                              <DropdownMenuItem onClick={async () => {
                                await axios.patch(`${API}/api/admin/quotes/${quote.id}/status?status=complete`, {}, authHeaders);
                                toast.success('Quote marked as complete'); fetchQuotes();
                              }} data-testid="quote-complete-btn">
                                <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" />Mark Complete
                              </DropdownMenuItem>
                            )}
                            {/* Convert to Invoice */}
                            {['accepted'].includes(quote.status) && (
                              <DropdownMenuItem onClick={() => {
                                setNewInvoice({
                                  customer_name: quote.customer_name, customer_email: quote.customer_email || '',
                                  customer_address: quote.customer_address || '', line_items: quote.line_items || [],
                                  notes: `From Quote ${quote.reference}`, payment_days: 7, description: '', date: null
                                });
                                setIsCreateInvoiceOpen(true);
                                toast.info('Quote copied to invoice');
                              }}><PoundSterling className="h-4 w-4 mr-2" />Convert to Invoice</DropdownMenuItem>
                            )}
                            {/* Reject - available unless already rejected/converted */}
                            {!['rejected', 'declined', 'converted_to_job', 'converted'].includes(quote.status) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleRejectQuote(quote)} className="text-red-600" data-testid="quote-reject-btn">
                                  <XCircle className="h-4 w-4 mr-2" />Reject Quote
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={async () => {
                              if (confirm('Delete this quote?')) {
                                await axios.delete(`${API}/api/admin/quotes/${quote.id}`, authHeaders);
                                toast.success('Quote deleted'); fetchQuotes();
                              }
                            }} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ==================== CREATE INVOICE DIALOG ==================== */}
      <Dialog open={isCreateInvoiceOpen} onOpenChange={setIsCreateInvoiceOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>
              Fill in customer, date, and line items. The reference is auto-assigned in SHS-YYYY-NNN format.
            </DialogDescription>
            {suggestedReference && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="invoice-suggested-reference">
                Next reference: <span className="font-mono font-semibold text-foreground">{suggestedReference}</span>
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Select value={newInvoice.customer_name} onValueChange={(v) => {
                  const customer = customers?.find(c => c.name === v);
                  setNewInvoice(prev => ({ ...prev, customer_name: v, customer_address: customer?.address || '', customer_email: customer?.email || '' }));
                }}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers?.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date (optional)</Label>
                <Input type="date" value={newInvoice.date || ''} onChange={(e) => setNewInvoice(prev => ({ ...prev, date: e.target.value }))} />
              </div>
            </div>
            <div><Label>Address</Label><Textarea value={newInvoice.customer_address} onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_address: e.target.value }))} /></div>
            <div><div className="flex items-center justify-between"><Label>Description</Label><AIWordingButton value={newInvoice.description} onApply={(t) => setNewInvoice(prev => ({ ...prev, description: t }))} context="the job description shown on an invoice" testId="ai-new-invoice-description" /></div><Textarea value={newInvoice.description} onChange={(e) => setNewInvoice(prev => ({ ...prev, description: e.target.value }))} /></div>
            <div>
              <Label>Line Items</Label>
              <LineItemsEditor items={newInvoice.line_items} onChange={(items) => setNewInvoice(prev => ({ ...prev, line_items: items }))} />
              <div className="flex flex-wrap gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => addLineItem(setNewInvoice)} data-testid="invoice-add-line-btn">+ Add Line</Button>
                <Button variant="outline" size="sm" onClick={() => setIsJobPickerOpen(true)} data-testid="invoice-pull-from-job-btn">
                  Pull from job (quote + received materials)
                </Button>
              </div>
            </div>
            
            {/* Deposit Configuration */}
            <DepositConfig data={newInvoice} onChange={setNewInvoice} />
            
            <div className="flex justify-between items-center pt-2 border-t">
              <div><Label>Payment Days</Label><Input type="number" value={newInvoice.payment_days} onChange={(e) => setNewInvoice(prev => ({ ...prev, payment_days: parseInt(e.target.value) || 7 }))} className="w-20" /></div>
              <p className="text-xl font-bold">Total: £{calculateTotal(newInvoice.line_items).toFixed(2)}</p>
            </div>

            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label>Custom terms / notes (this invoice only)</Label>
                <AIWordingButton value={newInvoice.terms_override || ''} onApply={(t) => setNewInvoice(prev => ({ ...prev, terms_override: t }))} context="custom payment terms / notes for one specific invoice" testId="ai-new-invoice-terms" />
              </div>
              <p className="text-xs text-muted-foreground mb-1">
                Optional — overrides the default invoice wording from Settings. Leave blank to use the default.
              </p>
              <Textarea
                value={newInvoice.terms_override || ''}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, terms_override: e.target.value }))}
                placeholder="e.g. 50% deposit required before work begins. Balance due on completion."
                rows={3}
                data-testid="new-invoice-terms-override"
              />
            </div>

            {/* Profitability widget — visible ABOVE total whenever a job_id is on the invoice */}
            {(() => {
              const target = (newInvoice.customer_name || '').trim().toLowerCase();
              const inferredJobId = newInvoice.job_id ||
                (enquiries || []).find(e => (e.name || '').trim().toLowerCase() === target)?.id;
              if (!inferredJobId) return null;
              return (
                <InvoiceProfitabilityWidget
                  authHeaders={authHeaders}
                  jobId={inferredJobId}
                  revenueOverride={calculateTotal(newInvoice.line_items)}
                />
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateInvoiceOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={isSavingInvoice}>{isSavingInvoice ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== JOB PICKER FOR "PULL FROM JOB" ==================== */}
      <Dialog open={isJobPickerOpen} onOpenChange={setIsJobPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="invoice-job-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select a job</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Pulls every received material + any accepted-quote line items onto this invoice. Review before creating.
            </p>
            <Input
              placeholder="Search by customer or service…"
              value={jobPickerSearch}
              onChange={(e) => setJobPickerSearch(e.target.value)}
              autoFocus
              data-testid="invoice-job-picker-search"
            />
            <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
              {(enquiries || [])
                .filter(j => {
                  const q = jobPickerSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (j.name || '').toLowerCase().includes(q) ||
                    (j.service || '').toLowerCase().includes(q) ||
                    (j.address || '').toLowerCase().includes(q);
                })
                .slice(0, 80)
                .map(j => (
                  <button
                    key={j.id}
                    type="button"
                    disabled={isPullingLines}
                    onClick={() => handlePullFromJob(j)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/50 disabled:opacity-50"
                    data-testid={`invoice-job-picker-option-${j.id}`}
                  >
                    <div className="font-medium text-sm">{j.name || 'Unnamed'}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {j.service || '—'} · {j.status || 'pending'} · £{(j.estimated_value || 0).toFixed(2)}
                    </div>
                  </button>
                ))}
              {(enquiries || []).length === 0 && (
                <div className="p-3 text-sm text-muted-foreground">No jobs available.</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== EDIT INVOICE DIALOG ==================== */}
      <Dialog open={isEditInvoiceOpen} onOpenChange={setIsEditInvoiceOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle>Edit Invoice {editingInvoice?.reference}</DialogTitle></DialogHeader>
          {editingInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Customer Name</Label><Input value={editingInvoice.customer_name} onChange={(e) => setEditingInvoice(prev => ({ ...prev, customer_name: e.target.value }))} /></div>
                <div><Label>Date</Label><Input type="date" value={editingInvoice.date || ''} onChange={(e) => setEditingInvoice(prev => ({ ...prev, date: e.target.value }))} /></div>
              </div>
              <div><Label>Address</Label><Textarea value={editingInvoice.customer_address} onChange={(e) => setEditingInvoice(prev => ({ ...prev, customer_address: e.target.value }))} /></div>
              <div><div className="flex items-center justify-between"><Label>Description</Label><AIWordingButton value={editingInvoice.description} onApply={(t) => setEditingInvoice(prev => ({ ...prev, description: t }))} context="the job description shown on an invoice" testId="ai-edit-invoice-description" /></div><Textarea value={editingInvoice.description} onChange={(e) => setEditingInvoice(prev => ({ ...prev, description: e.target.value }))} /></div>
              <div>
                <Label>Line Items</Label>
                <LineItemsEditor items={editingInvoice.line_items} onChange={(items) => setEditingInvoice(prev => ({ ...prev, line_items: items }))} />
                <Button variant="outline" size="sm" onClick={() => addLineItem(setEditingInvoice)} className="mt-2">+ Add Line</Button>
              </div>
              
              {/* Deposit Configuration */}
              <DepositConfig data={editingInvoice} onChange={setEditingInvoice} />

              {/* Iter 75: Payment Schedule — multi-stage payment requests
                  (deposit / mid / final etc).  Lives on the existing invoice,
                  no new invoice created.  Hides itself when the invoice is
                  brand-new and unsaved (no id) since the schedule endpoints
                  need a persisted invoice. */}
              {editingInvoice.id && (
                <div className="pt-2 border-t" data-testid="payment-schedule-section">
                  <Label className="text-base font-semibold">Payment Schedule</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Break this invoice into stages (deposit / mid / final).
                    Each stage can be sent as its own Payment Request PDF.
                  </p>
                  <PaymentScheduleEditor
                    invoiceId={editingInvoice.id}
                    authHeaders={authHeaders}
                    onChanged={() => { fetchInvoices(); }}
                  />
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t">
                <div><Label>Payment Days</Label><Input type="number" value={editingInvoice.payment_days} onChange={(e) => setEditingInvoice(prev => ({ ...prev, payment_days: parseInt(e.target.value) || 7 }))} className="w-20" /></div>
                <p className="text-xl font-bold">Total: £{calculateTotal(editingInvoice.line_items).toFixed(2)}</p>
              </div>

              <div className="pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label>Custom terms / notes (this invoice only)</Label>
                  <AIWordingButton value={editingInvoice.terms_override || ''} onApply={(t) => setEditingInvoice(prev => ({ ...prev, terms_override: t }))} context="custom payment terms / notes for one specific invoice" testId="ai-edit-invoice-terms" />
                </div>
                <p className="text-xs text-muted-foreground mb-1">
                  Overrides the default invoice wording from Settings for this invoice. Leave blank to use the default.
                </p>
                <Textarea
                  value={editingInvoice.terms_override || ''}
                  onChange={(e) => setEditingInvoice(prev => ({ ...prev, terms_override: e.target.value }))}
                  placeholder="e.g. 50% deposit required before work begins. Balance due on completion."
                  rows={3}
                  data-testid="edit-invoice-terms-override"
                />
              </div>

              {/* Profitability widget — live-recomputes from edited line items minus received materials */}
              {(() => {
                const target = (editingInvoice.customer_name || '').trim().toLowerCase();
                const inferredJobId = editingInvoice.job_id ||
                  (enquiries || []).find(e => (e.name || '').trim().toLowerCase() === target)?.id;
                if (!inferredJobId) return null;
                return (
                  <InvoiceProfitabilityWidget
                    authHeaders={authHeaders}
                    jobId={inferredJobId}
                    revenueOverride={calculateTotal(editingInvoice.line_items)}
                  />
                );
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditInvoiceOpen(false)}>Cancel</Button>
            <Button onClick={saveEditedInvoice} disabled={isSavingInvoice}>{isSavingInvoice ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save & Regenerate PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== CREATE QUOTE DIALOG ==================== */}
      <Dialog open={isCreateQuoteOpen} onOpenChange={setIsCreateQuoteOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Quote</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Select value={newQuote.customer_name} onValueChange={(v) => {
                  const customer = customers?.find(c => c.name === v);
                  setNewQuote(prev => ({ ...prev, customer_name: v, customer_address: customer?.address || '', customer_email: customer?.email || '' }));
                }}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers?.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Valid Days</Label><Input type="number" value={newQuote.valid_days} onChange={(e) => setNewQuote(prev => ({ ...prev, valid_days: parseInt(e.target.value) || 30 }))} /></div>
            </div>
            <div><Label>Address</Label><Textarea value={newQuote.customer_address} onChange={(e) => setNewQuote(prev => ({ ...prev, customer_address: e.target.value }))} /></div>
            <div><div className="flex items-center justify-between"><Label>Description</Label><AIWordingButton value={newQuote.description} onApply={(t) => setNewQuote(prev => ({ ...prev, description: t }))} context="the job description shown on a customer quote" testId="ai-new-quote-description" /></div><Textarea value={newQuote.description} onChange={(e) => setNewQuote(prev => ({ ...prev, description: e.target.value }))} /></div>
            <div>
              <Label>Line Items</Label>
              <LineItemsEditor items={newQuote.line_items} onChange={(items) => setNewQuote(prev => ({ ...prev, line_items: items }))} />
              <Button variant="outline" size="sm" onClick={() => addLineItem(setNewQuote)} className="mt-2">+ Add Line</Button>
            </div>
            <div className="flex justify-end pt-2 border-t">
              <p className="text-xl font-bold">Total: £{calculateTotal(newQuote.line_items).toFixed(2)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateQuoteOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateQuote} disabled={isSavingQuote}>{isSavingQuote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== EDIT QUOTE DIALOG ==================== */}
      <Dialog open={isEditQuoteOpen} onOpenChange={setIsEditQuoteOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Quote {editingQuote?.reference}</DialogTitle></DialogHeader>
          {editingQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Customer Name</Label><Input value={editingQuote.customer_name} onChange={(e) => setEditingQuote(prev => ({ ...prev, customer_name: e.target.value }))} /></div>
                <div><Label>Valid Days</Label><Input type="number" value={editingQuote.valid_days} onChange={(e) => setEditingQuote(prev => ({ ...prev, valid_days: parseInt(e.target.value) || 30 }))} /></div>
              </div>
              <div><Label>Address</Label><Textarea value={editingQuote.customer_address} onChange={(e) => setEditingQuote(prev => ({ ...prev, customer_address: e.target.value }))} /></div>
              <div><div className="flex items-center justify-between"><Label>Description</Label><AIWordingButton value={editingQuote.description} onApply={(t) => setEditingQuote(prev => ({ ...prev, description: t }))} context="the job description shown on a customer quote" testId="ai-edit-quote-description" /></div><Textarea value={editingQuote.description} onChange={(e) => setEditingQuote(prev => ({ ...prev, description: e.target.value }))} /></div>
              <div>
                <Label>Line Items</Label>
                <LineItemsEditor items={editingQuote.line_items} onChange={(items) => setEditingQuote(prev => ({ ...prev, line_items: items }))} />
                <Button variant="outline" size="sm" onClick={() => addLineItem(setEditingQuote)} className="mt-2">+ Add Line</Button>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <p className="text-xl font-bold">Total: £{calculateTotal(editingQuote.line_items).toFixed(2)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditQuoteOpen(false)}>Cancel</Button>
            <Button onClick={saveEditedQuote} disabled={isSavingQuote}>{isSavingQuote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save & Regenerate PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>
              {paymentData.type === 'deposit' ? 'Record Deposit Payment' : 
               paymentData.type === 'final' ? 'Record Final Payment' : 'Record Payment'}
            </DialogTitle>
          </DialogHeader>
          {paymentInvoice && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">Invoice:</span> <span className="font-medium">{paymentInvoice.reference}</span></p>
                <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{paymentInvoice.customer_name}</span></p>
                <p><span className="text-muted-foreground">Total:</span> <span className="font-bold">£{paymentInvoice.total?.toFixed(2)}</span></p>
                {paymentInvoice.amount_paid > 0 && (
                  <p><span className="text-muted-foreground">Already paid:</span> <span className="text-green-600 font-medium">£{paymentInvoice.amount_paid.toFixed(2)}</span></p>
                )}
              </div>

              {/* Profitability widget — placed directly above the Balance Due line */}
              {(() => {
                const target = (paymentInvoice.customer_name || '').trim().toLowerCase();
                const jobId = paymentInvoice.job_id ||
                  (enquiries || []).find(e => (e.name || '').trim().toLowerCase() === target)?.id;
                if (!jobId) return null;
                return (
                  <InvoiceProfitabilityWidget
                    authHeaders={authHeaders}
                    jobId={jobId}
                    revenueOverride={paymentInvoice.total || 0}
                  />
                );
              })()}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p><span className="text-muted-foreground">Balance Due:</span> <span className="font-bold text-amber-700 text-base">£{(paymentInvoice.balance_remaining ?? paymentInvoice.total)?.toFixed(2)}</span></p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>Amount (£)</Label>
                  <Input 
                    type="number" step="0.01" min="0.01"
                    value={paymentData.amount}
                    onChange={e => setPaymentData(p => ({ ...p, amount: e.target.value }))}
                    data-testid="payment-amount"
                  />
                </div>
                <div>
                  <Label>Date Paid</Label>
                  <Input 
                    type="date" 
                    value={paymentData.date}
                    onChange={e => setPaymentData(p => ({ ...p, date: e.target.value }))}
                    data-testid="payment-date"
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={paymentData.method} onValueChange={v => setPaymentData(p => ({ ...p, method: v }))}>
                    <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input 
                    value={paymentData.notes}
                    onChange={e => setPaymentData(p => ({ ...p, notes: e.target.value }))}
                    placeholder="e.g. Reference number, cheque details..."
                    data-testid="payment-notes"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={isRecordingPayment} data-testid="confirm-payment-btn">
              {isRecordingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk invoice OCR upload dialog */}
      <InvoiceBulkUploadDialog
        open={isBulkOcrOpen}
        onClose={() => setIsBulkOcrOpen(false)}
        authHeaders={{ headers: { Authorization: `Bearer ${token}` } }}
        onCommitted={() => { setIsBulkOcrOpen(false); onInvoiceCreated?.(); }}
      />

      {/* Bulk-edit invoice dates dialog — lets Sandy fix any invoices that were
          wrongly stamped with today's date pre-iteration-52. */}
      <InvoiceBulkDatesDialog
        open={isBulkDatesOpen}
        onClose={() => setIsBulkDatesOpen(false)}
        invoices={invoices}
        authHeaders={{ headers: { Authorization: `Bearer ${token}` } }}
        onSaved={() => { setIsBulkDatesOpen(false); onInvoiceCreated?.(); }}
      />
    </div>
  );
};

export default QuotesInvoicesTab;
